import { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useTRPCRequest } from '../../../hooks/useTRPCRequest';
import { TRPCMethodEnum, TRPCResourceEnum } from '../../../api/trpcApi/types';
import { ContainedButton } from '../../../components/buttons';
import {
  ModalComponent,
  ConfirmDeleteModalComponent
} from '../../../components/modals';
import { SearchBar } from '../../../components/search-bar';
import { Chip } from '../../../components/chips';

import { usePartners } from '../../partners/hooks/usePartners';
import { Partner } from '../../partners/types';
import { PartnerProduct } from '../../partnerProducts/types';
import { SizeEnum } from '../../../types';

interface PartnerAssignmentsSectionProps {
  productId: string;
  productSystemName: string;
}

export function PartnerAssignmentsSection({
  productId,
  productSystemName
}: PartnerAssignmentsSectionProps) {
  const { handleTRPCRequest } = useTRPCRequest();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  // Partner products for this product
  const [partnerProducts, setPartnerProducts] = useState<PartnerProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Modal state
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [isAddingPartner, setIsAddingPartner] = useState(false);
  const [confirmRemovePartner, setConfirmRemovePartner] = useState<
    string | null
  >(null);

  const {
    partners,
    isLoading: isLoadingPartners,
    refetch: refetchPartners
  } = usePartners();

  // Fetch partner products when component mounts or productId changes
  const fetchPartnerProducts = useCallback(async () => {
    if (!productId) return;

    setIsLoading(true);
    const { res, error } = await handleTRPCRequest({
      method: TRPCMethodEnum.listByProduct,
      resourceType: TRPCResourceEnum.partnerProducts,
      requestBody: { product_id: productId }
    });

    if (!error && res) {
      setPartnerProducts(res);
    }
    setIsLoading(false);
  }, [productId, handleTRPCRequest]);

  useEffect(() => {
    fetchPartnerProducts();
  }, [fetchPartnerProducts]);

  // Create a set of assigned partner IDs for quick lookup
  const assignedPartnerIds = new Set(partnerProducts.map(pp => pp.partner_id));

  // Add isAssigned flag to each partner
  const partnersWithAssignmentStatus = partners.map((partner: Partner) => ({
    ...partner,
    isAssigned: partner.partner_id
      ? assignedPartnerIds.has(partner.partner_id)
      : false
  }));

  // Filter by search term but keep all partners (assigned and unassigned)
  const filteredPartners = partnerSearch
    ? partnersWithAssignmentStatus.filter(
        partner =>
          partner.name.toLowerCase().includes(partnerSearch.toLowerCase()) ||
          partner.system_name
            ?.toLowerCase()
            .includes(partnerSearch.toLowerCase())
      )
    : partnersWithAssignmentStatus;

  const handleAddPartner = useCallback(
    async (partner: Partner) => {
      if (!partner.partner_id) return;

      setIsAddingPartner(true);
      const { res, error } = await handleTRPCRequest({
        method: TRPCMethodEnum.create,
        resourceType: TRPCResourceEnum.partnerProducts,
        requestBody: {
          partner_id: partner.partner_id,
          product_id: productId
        }
      });

      if (!error && res) {
        setPartnerProducts(prev => [
          ...prev,
          { ...res, partner_name: partner.name }
        ]);
        toast.success(`Partner "${partner.name}" assigned successfully!`);
      }
      setIsAddingPartner(false);
      setIsPartnerModalOpen(false);
      setPartnerSearch('');
    },
    [productId, handleTRPCRequest]
  );

  const handleRemovePartner = useCallback(
    async (partnerProductId: string) => {
      const { error } = await handleTRPCRequest({
        method: TRPCMethodEnum.delete,
        resourceType: TRPCResourceEnum.partnerProducts,
        requestBody: {
          partner_product_id: partnerProductId
        }
      });

      if (!error) {
        setPartnerProducts(prev =>
          prev.filter(pp => pp.partner_product_id !== partnerProductId)
        );
        toast.success('Partner assignment removed!');
      }
      setConfirmRemovePartner(null);
    },
    [handleTRPCRequest]
  );

  const handleOpenPartnerModal = useCallback(() => {
    setIsPartnerModalOpen(true);
    refetchPartners();
  }, [refetchPartners]);

  return (
    <div className="card mb-5">
      {/* Header - Always visible */}
      <button
        className="flex w-full items-center justify-between p-5"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">Partner Assignments</h2>
          <Chip backgroundColor="bg-gray-500" size={SizeEnum.small}>
            {partnerProducts.length}
          </Chip>
          {partnerProducts.length > 0 && (
            <span className="text-light-gray text-sm">
              Used by {partnerProducts.length} partner
              {partnerProducts.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <span className="text-2xl text-gray-400">{isExpanded ? '−' : '+'}</span>
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="border-t px-5 pb-5">
          <div className="mb-4 mt-4 flex items-center justify-end">
            <ContainedButton onClick={handleOpenPartnerModal}>
              + Assign to Partner
            </ContainedButton>
          </div>

          {isLoading ? (
            <div className="text-light-gray py-8 text-center">Loading...</div>
          ) : partnerProducts.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              This product is not assigned to any partners.
              <br />
              <button
                className="mt-2 text-blue-500 hover:underline"
                onClick={handleOpenPartnerModal}
              >
                Assign to a partner
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-semibold">Partner Name</th>
                  <th className="pb-2 font-semibold">Customized</th>
                  <th className="pb-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {partnerProducts.map(pp => (
                  <tr
                    key={pp.partner_product_id}
                    className="border-b last:border-b-0"
                  >
                    <td className="py-3">
                      <Link
                        to={`/partners/${pp.partner_id}`}
                        className="text-navy hover:underline"
                      >
                        {pp.partner_name || pp.partner_id}
                      </Link>
                    </td>
                    <td className="py-3">
                      {(pp.style_count ?? 0) > 0 ? (
                        <span className="text-turquoise text-sm">
                          ✓ {pp.style_count} style
                          {(pp.style_count ?? 0) !== 1 ? 's' : ''} selected
                        </span>
                      ) : (
                        <span className="text-light-gray text-sm italic">
                          Using defaults
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-right">
                      <button
                        onClick={() =>
                          navigate(
                            `/partners/${pp.partner_id}/products/${productId}`,
                            { state: { from: 'product' } }
                          )
                        }
                        className="text-navy mr-4 text-sm hover:underline"
                      >
                        Configure
                      </button>
                      <button
                        className="text-red text-sm hover:underline"
                        onClick={() =>
                          setConfirmRemovePartner(pp.partner_product_id)
                        }
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Partner Selection Modal */}
      <ModalComponent
        isOpen={isPartnerModalOpen}
        onRequestClose={() => {
          setIsPartnerModalOpen(false);
          setPartnerSearch('');
        }}
        contentLabel="Assign to Partner"
        className="w-[600px]"
      >
        <h2 className="mb-4 text-lg font-semibold">
          Assign "{productSystemName}" to Partner
        </h2>
        <p className="text-light-gray mb-4 text-sm">
          Select a partner to assign this product to. This will create a partner
          product configuration.
        </p>
        <SearchBar
          placeHolder="Search partners..."
          searchTerm={partnerSearch}
          onSearch={setPartnerSearch}
          searchOnChange
          disabled={isLoadingPartners}
        />
        <div className="mt-4 max-h-96 overflow-y-auto">
          {isLoadingPartners ? (
            <div className="text-light-gray py-4 text-center">Loading...</div>
          ) : filteredPartners.length === 0 ? (
            <div className="text-light-gray py-4 text-center">
              {partnerSearch
                ? 'No partners found matching your search.'
                : 'No partners available.'}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredPartners.map(partner => (
                <div
                  key={partner.partner_id}
                  className={`flex items-center justify-between rounded border p-3 ${
                    partner.isAssigned
                      ? 'cursor-not-allowed bg-gray-100 opacity-60'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div>
                    <div
                      className={`font-medium ${
                        partner.isAssigned ? 'text-gray-500' : ''
                      }`}
                    >
                      {partner.name}
                    </div>
                  </div>
                  {partner.isAssigned ? (
                    <span className="text-sm italic text-gray-500">
                      Already assigned
                    </span>
                  ) : (
                    <ContainedButton
                      size={SizeEnum.small}
                      onClick={() => handleAddPartner(partner)}
                      disabled={isAddingPartner}
                    >
                      Assign
                    </ContainedButton>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <ContainedButton
            color="secondary"
            onClick={() => {
              setIsPartnerModalOpen(false);
              setPartnerSearch('');
            }}
          >
            Cancel
          </ContainedButton>
        </div>
      </ModalComponent>

      {/* Confirm Remove Partner Modal */}
      <ConfirmDeleteModalComponent
        isOpen={!!confirmRemovePartner}
        entityName="this partner assignment"
        onCancel={() => setConfirmRemovePartner(null)}
        onConfirm={() =>
          confirmRemovePartner && handleRemovePartner(confirmRemovePartner)
        }
      />
    </div>
  );
}
